import { useState, CSSProperties, useEffect, useContext } from 'react';
import { ViewTypes } from 'modules/Types';
import SwitchSymphony from 'elements/SwitchSymphony';
import CustomTinyMCEEditor from 'components/shareable/CustomTinyMCEEditor/CustomTinyMCEEditor';
import useMessageYourFans from 'Hooks/useMessageYourFans';
import useCurrentBrand, { BrandExistsState } from 'Hooks/useCurrentBrand';
import { styx, clsx } from 'modules/Utils';
import Sty from 'modules/Sty';
import CardSymphony from 'elements/CardSymphony';
import Colors from 'modules/Colors';
import InputSymphony from 'elements/InputSymphony';
import TabsSymphony, { TabItem } from 'elements/TabsSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';
import useCurrentUser, { UserExistsState } from 'Hooks/useCurrentUser';
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext';
import { MAX_SUBJECT_LINE_LENGTH } from 'pages/FansPage/constants';
import { getInitialRawEmailTemplate } from 'pages/FansPage/utils';
import { getRandomSubjectLine } from 'pages/FansPage/utils';

const { DESKTOP, MOBILE } = ViewTypes;

const MESSAGE_TABS = [
    {
        id: 'email',
        title: 'Email Message'
    },
    // {
    //     id: 'sms',
    //     title: 'SMS Message'
    // }
]

export type MessageType = 'email' | 'sms';

interface DesignTabProps {
    className?: string;
    style?: CSSProperties;
}

const DesignTab = ({
    className,
    style
}: DesignTabProps) => {
    const { handleOpenModal } = useContext(UpgradeModalContext);
    const {
        design,
        setDesign,
        setEmailTemplate,
        setSubjectLine,
        isPoweredBySymphony,
        setIsPoweredBySymphony
    } = useMessageYourFans();

    const {
        currentUser
    } = useCurrentUser() as UserExistsState;

    const {
        currentBrand,
        getBrandImage,
        isProBrand
    } = useCurrentBrand() as BrandExistsState;
    const { subjectLine, emailTemplate } = design;

    const [isDesktopPreview, setIsDesktopPreview] = useState<ViewTypes>(DESKTOP);
    const [currentMessageTab, setCurrentMessageTab] = useState<TabItem>(MESSAGE_TABS[0]);

    const isEmailEnabled = true;
    // email true by default until we add sms
    // const isEmailEnabled = messageTypes.includes('email');
    // const isSMSEnabled = messageTypes.includes('sms');

    useEffect(() => {
        setSubjectLine(getRandomSubjectLine(currentBrand.name));

        const initialEmailTemplate = getInitialRawEmailTemplate({
            brandName: currentBrand.name,
            brandImage: getBrandImage(),
            preview: undefined
        });

        setIsPoweredBySymphony(true)
        setEmailTemplate(initialEmailTemplate);
    }, []);

    const toggleMobileDesktopPreview = (newPreview: ViewTypes) => {
        const newTemplate = isDesktopPreview === DESKTOP
            ? (emailTemplate as string).replace("90%", "50%")
            : (emailTemplate as string).replace("50%", "90%");

        setDesign({
            ...design,
            emailTemplate: newTemplate,
        });
        setIsDesktopPreview(newPreview);
    };

    const handleChangeSubjectLine = (value: string) => {
        setDesign({
            ...design,
            subjectLine: value
        });
    };

    const handleSaveTemplate = (newTemplate?: string) => {
        if (!newTemplate) return;
        if (currentMessageTab.id === 'email') {
            setDesign({
                ...design,
                emailTemplate: newTemplate,
            });
        }
    };

    const renderLeftColumn = () => {
        return (
            <div className="designTabLeftColumn" style={sty.designTabLeftColumn}>
                <CardSymphony className="designTabCard">
                    <CardSymphony
                        className="designTabHeader"
                        title="Design your Message"
                        subtitle="Message your fans with a customized email"
                        isNoPadding
                        size="small"
                    />
                </CardSymphony>

                <CardSymphony className="designTabMessageTypeRow">
                    {/* <SwitchSymphony
                        className="designTabMessageTypeSwitch"
                        checked={isEmailEnabled}
                        label="Email"
                        labelPlacement="right"
                        labelStyle={Sty.text18}
                        // onChange={handleEmailToggle}
                    /> */}
                    <div style={{ ...Sty.text18bold, marginBottom: 8 }}>Email Options</div>
                    <div className="designTabMessageTypeRow" style={{ display: 'flex', gap: 16 }}>
                        <div className="designTabMessageTypeSection" style={sty.designTabMessageTypeSection}>
                            <div className='referralContainer' style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <div className='referralTitle' style={Sty.text14}>
                                    Powered by Symphony
                                </div>
                                <Icon className='referralGem' icon="gem" color="purple" />
                                {!isProBrand &&
                                    <ButtonSymphony
                                        className="designTabUpgradeToProButton"
                                        onClick={handleOpenModal}
                                        variant="text"
                                        size="small"
                                    >
                                        Upgrade to Pro
                                    </ButtonSymphony>
                                }
                            </div>
                            <div className='referralSubtitle' style={Sty.text12grey}>
                                We will automatically include your referral link
                            </div>
                            <ButtonSymphony
                                className="designTabLearnMore"
                                href="https://support.symphonyos.co/en/articles/7002468-symphony-referral-program-faqs"
                                variant="link"
                                target="_blank"
                                size="small"
                            >
                                Learn more
                            </ButtonSymphony>
                        </div>
                        <div onClick={() => {
                            if (!isProBrand) {
                                handleOpenModal()
                            }
                        }}>
                            <SwitchSymphony
                                className="designTabMessageTypeSwitch"
                                checked={isPoweredBySymphony}
                                onChange={setIsPoweredBySymphony}
                                disabled={!isProBrand}
                            />
                        </div>
                    </div>
                </CardSymphony>
            </div>
        )
    }

    const renderPoweredBySymphonyFooter = () => {
        return (
            <div
                className="designTabPoweredBySymphony"
                style={styx(
                    sty.designTabPoweredBySymphony,
                    isDesktopPreview === MOBILE ? sty.isMobilePreviewStyle : null,
                )}
            >
                <div
                    className="designTabPoweredBySymphonyTitle"
                >
                    <a href={currentUser.metadata?.rewardful_affiliate_link} style={{ display: 'inline-flex', alignItems: 'center' }}>Powered by <img className="designTabPoweredBySymphonyImage" src="https://symphony.to/logos/symphony-badge-purple.png" style={{ height: 24, verticalAlign: 'middle', marginLeft: 8 }} /></a>
                </div>
            </div>
        )
    }

    const renderTogglePreview = () => {
        return (
            <div
                className="designTabTogglePreviewWrapper"
                style={sty.designTabTogglePreviewWrapper}
            >
                <Icon
                    className="designTabTogglePreviewIcon"
                    icon="computer"
                    onClick={() => toggleMobileDesktopPreview(DESKTOP)}
                    style={{
                        opacity: isDesktopPreview === DESKTOP ? 1 : 0.3
                    }}
                />
                <Icon
                    className="designTabTogglePreviewIcon"
                    icon="mobile"
                    onClick={() => toggleMobileDesktopPreview(MOBILE)}
                    style={{
                        opacity: isDesktopPreview === MOBILE ? 1 : 0.3
                    }}
                />
            </div>
        )
    }

    const renderRightColumn = () => {
        return (
            <div
                className="designTabRightColumn"
                style={sty.designTabRightColumn}
            >
                <TabsSymphony
                    className="designTabTabs"
                    selectedTab={currentMessageTab}
                    onChange={(tab) => setCurrentMessageTab(tab as TabItem)}
                    tabs={MESSAGE_TABS}
                    style={{
                        paddingTop: 8,
                        width: '100%',
                        backgroundColor: 'white',
                        justifyContent: 'center',
                        boxShadow: Sty.boxShadowBottom
                    }}
                />
                <div
                    className="designTabEditorContainer"
                    style={sty.designTabEditorContainer}
                >
                    {currentMessageTab.id === 'email' &&
                        <div
                            className="designTabInputContainer"
                            style={sty.designTabInputContainer}
                        >
                            <InputSymphony
                                className="designTabInput"
                                type="text"
                                value={subjectLine}
                                onChange={handleChangeSubjectLine}
                                maxLength={MAX_SUBJECT_LINE_LENGTH}
                                styleInput={{border: 'none'}}
                            />
                            {renderTogglePreview()}
                        </div>
                    }
                    <CustomTinyMCEEditor
                        className="designTabEditor"
                        saveTemplate={handleSaveTemplate}
                        apiKey={process.env.REACT_APP_TINY_MCE_API_KEY}
                        initialValue={design.emailTemplate}
                        isSMSMode={false}
                        style={styx(
                            isDesktopPreview === MOBILE ? sty.isMobilePreviewStyle : null,
                            { marginLeft: 'auto', marginRight: 'auto' }
                        )}
                    />
                    {isPoweredBySymphony &&
                        renderPoweredBySymphonyFooter()
                    }
                </div>
            </div>
        )
    }

    return (
        <div
            className={clsx('designTabComponent', className)}
            style={styx(sty.designTabComponent, style)}
        >
            {renderLeftColumn()}
            {renderRightColumn()}
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    designTabComponent: {
        display: 'flex',
        gap: 16
    },
    customInput: {
        backgroundColor: 'white',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: 4,
        outline: 'none',
        padding: 12,
        paddingRight: 65,
        position: 'relative',
        transition: 'border-color 0.2s'
    },
    infoTitle: {
        ...Sty.text18grey,
        fontWeight: 600
    },
    designTabInputContainer: {
        alignItems: 'center',
        display: 'flex',
        gap: 16,
    },
    learnMore: {
        ...Sty.text12,
        color: '#2196F3',
        cursor: 'pointer',
        marginLeft: 4
    },
    messageTypeSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4
    },
    designTabLeftColumn: {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 8,
        gap: 16,
        flex: 3,
    },
    designTabRightColumn: {
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        backgroundColor: Colors.grey2,
        overflowY: 'scroll',
        borderRadius: 16,
        flex: 4
    },
    symphonyToggleRow: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
        marginTop: 8
    },
    tabContainer: {
        borderBottom: '1px solid rgb(229, 231, 235)',
        display: 'flex',
        justifyContent: 'center'
    },
    designTabTogglePreviewWrapper: {
        display: 'flex',
        gap: 8,
        backgroundColor: 'white',
        borderRadius: 8,
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 16,
        paddingRight: 16,
        flexShrink: 0,
        boxShadow: Sty.boxShadowBottom
    },
    designTabEditorContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 16
    },
    designTabMessageTypeSection: {
        paddingLeft: 44
    },

    designTabPoweredBySymphony: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        backgroundColor: 'white',
        gap: 4,
        padding: 16,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        boxShadow: Sty.boxShadowBottom,
        marginTop: -24,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%'
    },
    isMobilePreviewStyle: {
        width: 340,
    }
};

export default DesignTab; 